import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import type {
  En18120DfrAssessmentResult,
  LegacyPackagingRecyclabilityAssessmentStoredEntry,
  PackagingRecyclabilityAssessmentStoredEntry,
  PaperRecyclabilityAssessmentResult,
  RecyclabilityDisqualifier,
  RecyclabilityPenalty,
  UnsupportedRecyclabilityAssessmentResult,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  isEn18120DfrAssessmentEntry,
  isLegacyRecyclabilityAssessmentEntry,
  isPaperRecyclabilityAssessmentEntry,
  isUnsupportedRecyclabilityAssessmentEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import {
  pickPackagingAssessmentProse,
  pickPackagingAssessmentProseList,
} from './pickPackagingAssessmentProse'

export function formatRunTimestamp(timestamp: string, locale: string): string {
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) {
    return timestamp
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

type PackagingRecyclabilityAssessmentRunResultsProps = {
  result: PackagingRecyclabilityAssessmentStoredEntry
  title?: string
}

function decisionBadgeVariant(
  decision: 'GREEN' | 'YELLOW' | 'RED' | null
): 'success' | 'secondary' | 'destructive' | 'outline' {
  if (decision === 'GREEN') {
    return 'success'
  }
  if (decision === 'YELLOW') {
    return 'secondary'
  }
  if (decision === 'RED') {
    return 'destructive'
  }
  return 'outline'
}

function penaltyNote(
  penalty: RecyclabilityPenalty,
  useChinese: boolean
): string {
  if ('noteEn' in penalty) {
    return pickPackagingAssessmentProse({
      en: penalty.noteEn,
      zh: penalty.noteZh,
      useChinese,
    })
  }
  return penalty.note
}

function disqualifierNote(
  disqualifier: RecyclabilityDisqualifier,
  useChinese: boolean
): string {
  if ('noteEn' in disqualifier) {
    return pickPackagingAssessmentProse({
      en: disqualifier.noteEn,
      zh: disqualifier.noteZh,
      useChinese,
    })
  }
  return disqualifier.note
}

function PaperClassResults(props: {
  result: PaperRecyclabilityAssessmentResult
  assessmentVersion: string
  useChinese: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const { result, assessmentVersion, useChinese } = props
  const showRecyclableBadge = result.isRecyclable
  const showNotRecyclableBadge = !result.isRecyclable
  const classReasoning = pickPackagingAssessmentProse({
    en: 'classReasoningEn' in result ? result.classReasoningEn : undefined,
    zh: 'classReasoningZh' in result ? result.classReasoningZh : undefined,
    legacy: 'classReasoning' in result ? result.classReasoning : undefined,
    useChinese,
  })
  const isRecyclableReasoning = pickPackagingAssessmentProse({
    en:
      'isRecyclableReasoningEn' in result
        ? result.isRecyclableReasoningEn
        : undefined,
    zh:
      'isRecyclableReasoningZh' in result
        ? result.isRecyclableReasoningZh
        : undefined,
    legacy:
      'isRecyclableReasoning' in result
        ? result.isRecyclableReasoning
        : undefined,
    useChinese,
  })
  const dataIssues = pickPackagingAssessmentProseList({
    en: 'dataIssuesEn' in result ? result.dataIssuesEn : undefined,
    zh: 'dataIssuesZh' in result ? result.dataIssuesZh : undefined,
    legacy: 'dataIssues' in result ? result.dataIssues : undefined,
    useChinese,
  })
  const missingInformation = pickPackagingAssessmentProseList({
    en:
      'missingInformationEn' in result ? result.missingInformationEn : undefined,
    zh:
      'missingInformationZh' in result ? result.missingInformationZh : undefined,
    legacy:
      'missingInformation' in result ? result.missingInformation : undefined,
    useChinese,
  })

  return (
    <>
      <div className='flex flex-wrap items-center gap-2'>
        {showRecyclableBadge && (
          <Badge variant='success'>
            {t('itemDetail.recyclability.recyclable')}
          </Badge>
        )}
        {showNotRecyclableBadge && (
          <Badge variant='destructive'>
            {t('itemDetail.recyclability.notRecyclable')}
          </Badge>
        )}
        <Badge variant='outline'>
          {t('itemDetail.recyclability.classLabel', {
            class: result.recyclabilityClass,
          })}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.recyclability.confidence', {
            level: result.confidence,
          })}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.recyclability.version', {
            version: assessmentVersion,
          })}
        </Badge>
      </div>

      <dl className='grid gap-3 text-sm md:grid-cols-2'>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.targetStream')}
          </dt>
          <dd>{result.targetedRecyclingStream}</dd>
        </div>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.dominantMaterial')}
          </dt>
          <dd>{result.dominantMaterial}</dd>
        </div>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.recyclableContentPct')}
          </dt>
          <dd>
            {result.recyclableContentPct != null
              ? `${result.recyclableContentPct}%`
              : t('common:notAvailable')}
          </dd>
        </div>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.baseClass')}
          </dt>
          <dd>{result.baseClass}</dd>
        </div>
      </dl>

      {result.disqualifier != null && (
        <div className='space-y-1 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm'>
          <p className='font-medium'>
            {t('itemDetail.recyclability.disqualifierTitle')}
          </p>
          <p className='text-muted-foreground'>
            {disqualifierNote(result.disqualifier, useChinese)}
          </p>
        </div>
      )}

      {result.penalties.length > 0 && (
        <div className='space-y-2'>
          <p className='text-sm font-medium'>
            {t('itemDetail.recyclability.penaltiesTitle')}
          </p>
          <ul className='space-y-2'>
            {result.penalties.map((penalty, index) => (
              <li
                key={`${penalty.type}-${index}`}
                className='rounded-md border p-3 text-sm text-muted-foreground'
              >
                <p className='font-medium text-foreground'>{penalty.type}</p>
                <p>{penaltyNote(penalty, useChinese)}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className='space-y-2 text-sm'>
        <div>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.classReasoning')}
          </p>
          <p className='text-muted-foreground'>{classReasoning}</p>
        </div>
        <div>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.isRecyclableReasoning')}
          </p>
          <p className='text-muted-foreground'>{isRecyclableReasoning}</p>
        </div>
      </div>

      <IssuesLists
        dataIssues={dataIssues}
        missingInformation={missingInformation}
      />
    </>
  )
}

function En18120DfrResults(props: {
  result: En18120DfrAssessmentResult
  assessmentVersion: string
  useChinese: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const { result, assessmentVersion, useChinese } = props
  const isRecyclable = result.isRecyclable ?? result.overallCompatible
  const showRecyclableBadge = isRecyclable
  const showNotRecyclableBadge = !isRecyclable
  const recyclabilityGrade = result.recyclabilityGrade
  let gradeBadgeLabel: string | null = null
  if (recyclabilityGrade === 'TECHNICALLY_NON_RECYCLABLE') {
    gradeBadgeLabel = t('itemDetail.recyclability.technicallyNonRecyclable')
  } else if (
    recyclabilityGrade === 'A' ||
    recyclabilityGrade === 'B' ||
    recyclabilityGrade === 'C'
  ) {
    gradeBadgeLabel = t('itemDetail.recyclability.gradeLabel', {
      grade: recyclabilityGrade,
    })
  }
  const selectionReasoning = pickPackagingAssessmentProse({
    en:
      'selectionReasoningEn' in result
        ? result.selectionReasoningEn
        : undefined,
    zh:
      'selectionReasoningZh' in result
        ? result.selectionReasoningZh
        : undefined,
    legacy:
      'selectionReasoning' in result ? result.selectionReasoning : undefined,
    useChinese,
  })
  const overallReasoning = pickPackagingAssessmentProse({
    en:
      'overallReasoningEn' in result ? result.overallReasoningEn : undefined,
    zh:
      'overallReasoningZh' in result ? result.overallReasoningZh : undefined,
    legacy:
      'overallReasoning' in result ? result.overallReasoning : undefined,
    useChinese,
  })
  const dataIssues = pickPackagingAssessmentProseList({
    en: 'dataIssuesEn' in result ? result.dataIssuesEn : undefined,
    zh: 'dataIssuesZh' in result ? result.dataIssuesZh : undefined,
    legacy: 'dataIssues' in result ? result.dataIssues : undefined,
    useChinese,
  })
  const missingInformation = pickPackagingAssessmentProseList({
    en:
      'missingInformationEn' in result ? result.missingInformationEn : undefined,
    zh:
      'missingInformationZh' in result ? result.missingInformationZh : undefined,
    legacy:
      'missingInformation' in result ? result.missingInformation : undefined,
    useChinese,
  })

  return (
    <>
      <div className='flex flex-wrap items-center gap-2'>
        {showRecyclableBadge && (
          <Badge variant='success'>
            {t('itemDetail.recyclability.recyclable')}
          </Badge>
        )}
        {showNotRecyclableBadge && (
          <Badge variant='destructive'>
            {t('itemDetail.recyclability.notRecyclable')}
          </Badge>
        )}
        {gradeBadgeLabel != null && (
          <Badge variant='outline'>{gradeBadgeLabel}</Badge>
        )}
        <Badge variant={result.overallCompatible ? 'success' : 'destructive'}>
          {result.overallCompatible
            ? t('itemDetail.recyclability.en18120.compatible')
            : t('itemDetail.recyclability.en18120.notCompatible')}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.recyclability.confidence', {
            level: result.confidence,
          })}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.recyclability.version', {
            version: assessmentVersion,
          })}
        </Badge>
      </div>

      <dl className='grid gap-3 text-sm md:grid-cols-2'>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.en18120.targetStream')}
          </dt>
          <dd>{result.targetedStream}</dd>
        </div>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.dominantMaterial')}
          </dt>
          <dd>{result.dominantMaterial}</dd>
        </div>
        <div>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.recyclableContentPct')}
          </dt>
          <dd>
            {result.recyclableContentPct != null
              ? `${result.recyclableContentPct}%`
              : t('common:notAvailable')}
          </dd>
        </div>
        <div className='md:col-span-2'>
          <dt className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.en18120.tables')}
          </dt>
          <dd>{result.tableIds.join(', ')}</dd>
        </div>
      </dl>

      {selectionReasoning.length > 0 ? (
        <div className='text-sm'>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            {t('itemDetail.recyclability.en18120.selectionReasoning')}
          </p>
          <p className='text-muted-foreground'>{selectionReasoning}</p>
        </div>
      ) : null}

      <div className='space-y-2'>
        <p className='text-sm font-medium'>
          {t('itemDetail.recyclability.en18120.categoriesTitle')}
        </p>
        <ul className='space-y-2'>
          {result.categories.map((category) => {
            const categoryReasoning = pickPackagingAssessmentProse({
              en: 'reasoningEn' in category ? category.reasoningEn : undefined,
              zh: 'reasoningZh' in category ? category.reasoningZh : undefined,
              legacy: 'reasoning' in category ? category.reasoning : undefined,
              useChinese,
            })
            return (
              <li
                key={category.categoryName}
                className='rounded-md border p-3 text-sm'
              >
                <div className='mb-1 flex flex-wrap items-center gap-2'>
                  <span className='font-medium'>{category.categoryName}</span>
                  <Badge variant={decisionBadgeVariant(category.decision)}>
                    {category.decision ??
                      t('itemDetail.recyclability.en18120.notApplicable')}
                  </Badge>
                </div>
                <p className='text-muted-foreground'>{categoryReasoning}</p>
                {category.matchedRule ? (
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {category.matchedRule}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      </div>

      <div className='text-sm'>
        <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
          {t('itemDetail.recyclability.en18120.overallReasoning')}
        </p>
        <p className='text-muted-foreground'>{overallReasoning}</p>
      </div>

      <IssuesLists
        dataIssues={dataIssues}
        missingInformation={missingInformation}
      />
    </>
  )
}

function UnsupportedResults(props: {
  result: UnsupportedRecyclabilityAssessmentResult
  assessmentVersion: string
  useChinese: boolean
}) {
  const { t } = useTranslation('packagingSuppliers')
  const { result, assessmentVersion, useChinese } = props
  const reasoning = pickPackagingAssessmentProse({
    en: 'reasoningEn' in result ? result.reasoningEn : undefined,
    zh: 'reasoningZh' in result ? result.reasoningZh : undefined,
    legacy: 'reasoning' in result ? result.reasoning : undefined,
    useChinese,
  })

  return (
    <>
      <div className='flex flex-wrap items-center gap-2'>
        <Badge variant='secondary'>
          {t('itemDetail.recyclability.unsupported.badge')}
        </Badge>
        <Badge variant='outline'>
          {t('itemDetail.recyclability.version', {
            version: assessmentVersion,
          })}
        </Badge>
      </div>
      <p className='text-sm text-muted-foreground'>{reasoning}</p>
      {result.dominantMaterial != null && (
        <p className='text-sm'>
          <span className='text-muted-foreground'>
            {t('itemDetail.recyclability.dominantMaterial')}:{' '}
          </span>
          {result.dominantMaterial}
        </p>
      )}
    </>
  )
}

function LegacyClassResults(props: {
  result: LegacyPackagingRecyclabilityAssessmentStoredEntry
  useChinese: boolean
}) {
  const {
    assessmentVersion,
    isRecyclable,
    recyclabilityClass,
    targetedRecyclingStream,
    dominantMaterial,
    recyclableContentPct,
    weightBasis,
    baseClass,
    penalties,
    disqualifier,
    confidence,
    dataIssues,
    missingInformation,
    classReasoning,
    isRecyclableReasoning,
  } = props.result

  return (
    <PaperClassResults
      assessmentVersion={assessmentVersion}
      useChinese={props.useChinese}
      result={
        {
          methodology: 'EN_643',
          isRecyclable,
          recyclabilityClass,
          targetedRecyclingStream,
          dominantMaterial,
          recyclableContentPct,
          weightBasis,
          baseClass,
          penalties,
          disqualifier,
          confidence,
          dataIssues,
          missingInformation,
          classReasoning,
          isRecyclableReasoning,
        } as PaperRecyclabilityAssessmentResult
      }
    />
  )
}

function IssuesLists(props: {
  dataIssues: string[]
  missingInformation: string[]
}) {
  const { t } = useTranslation('packagingSuppliers')
  const { dataIssues, missingInformation } = props

  return (
    <>
      {dataIssues.length > 0 && (
        <div className='space-y-1 text-sm'>
          <p className='font-medium'>{t('itemDetail.recyclability.dataIssues')}</p>
          <ul className='list-disc ps-5 text-muted-foreground'>
            {dataIssues.map((issue, index) => (
              <li key={`${issue}-${index}`}>{issue}</li>
            ))}
          </ul>
        </div>
      )}

      {missingInformation.length > 0 && (
        <div className='space-y-1 text-sm'>
          <p className='font-medium'>
            {t('itemDetail.recyclability.missingInformation')}
          </p>
          <ul className='list-disc ps-5 text-muted-foreground'>
            {missingInformation.map((info, index) => (
              <li key={`${info}-${index}`}>{info}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}

export function PackagingRecyclabilityAssessmentRunResults({
  result,
  title,
}: PackagingRecyclabilityAssessmentRunResultsProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const useChinese = i18n.language.startsWith('zh')

  return (
    <div className='space-y-4 rounded-md border p-4'>
      <div className='flex flex-wrap items-center gap-2'>
        {title ? <span className='text-sm font-medium'>{title}</span> : null}
        {!title ? (
          <span className='text-sm font-medium'>
            {t('itemDetail.recyclability.resultTitle')}
          </span>
        ) : null}
      </div>

      {isPaperRecyclabilityAssessmentEntry(result) && (
        <PaperClassResults
          assessmentVersion={result.assessmentVersion}
          result={result.result}
          useChinese={useChinese}
        />
      )}
      {isEn18120DfrAssessmentEntry(result) && (
        <En18120DfrResults
          assessmentVersion={result.assessmentVersion}
          result={result.result}
          useChinese={useChinese}
        />
      )}
      {isUnsupportedRecyclabilityAssessmentEntry(result) && (
        <UnsupportedResults
          assessmentVersion={result.assessmentVersion}
          result={result.result}
          useChinese={useChinese}
        />
      )}
      {isLegacyRecyclabilityAssessmentEntry(result) && (
        <LegacyClassResults result={result} useChinese={useChinese} />
      )}
    </div>
  )
}
